import { Box } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DatePickerGlobalStyles } from "./DatePickerGlobalStyles";
import { DatePickerHeader } from "./DatePickerHeader";
import {
  PORTAL_ID,
  PickerCustomInput,
  datepickerLocaleMap,
  parsePickerIsoDate,
  useDatepickerPortal,
} from "./pickerInternals";

interface ThemedDateTimePickerProps {
  id?: string;
  /**
   * Accessible name for the underlying input. Required when the caller renders no
   * visible <label htmlFor> (e.g. the milestone timeline hides the label), otherwise
   * screen-reader users land on an unnamed date field.
   */
  ariaLabel?: string;
  defaultDate?: string;
  date?: string | null;
  onChange: (isoDateTime?: string) => Promise<void> | void;
  onBlur?: () => Promise<void> | void;
  isDisabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  timeIntervals?: number;
  timeCaption?: string;
  /**
   * Local-time fallback applied when the user picks a date in the calendar
   * without choosing a time (react-datepicker emits 00:00:00.000 in that case).
   * Tender deadlines pass {hours: 23, minutes: 59} so picking just a date
   * means "end of that day". Explicit time-list selections are preserved.
   */
  defaultTime?: { hours: number; minutes: number };
  /**
   * Render the calendar directly without the text input field — useful when the
   * caller already shows the chosen date elsewhere and wants the picker to skip
   * the input step (e.g. inside a Popover triggered by an icon button).
   */
  inline?: boolean;
}

const withDefaultTime = (
  d: Date,
  defaultTime: { hours: number; minutes: number },
): Date => {
  if (d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0) {
    return d;
  }
  const out = new Date(d);
  out.setHours(defaultTime.hours, defaultTime.minutes, 0, 0);
  return out;
};

export const ThemedDateTimePicker = ({
  id,
  ariaLabel,
  defaultDate,
  date,
  onChange,
  onBlur,
  isDisabled,
  autoFocus = false,
  placeholder,
  minDate,
  maxDate,
  timeIntervals = 30,
  timeCaption,
  defaultTime,
  inline = false,
}: ThemedDateTimePickerProps) => {
  const { i18n, t } = useTranslation();
  const intlLocale = datepickerLocaleMap[i18n.language] ?? "nb-NO";
  const resolvedTimeCaption = timeCaption ?? t("Time") ?? "Time";
  const [internalDate, setInternalDate] = useState<Date | null>(
    parsePickerIsoDate(defaultDate, { warnOnInvalid: true }),
  );
  const selected =
    date !== undefined ? parsePickerIsoDate(date, { warnOnInvalid: true }) : internalDate;
  const pickerRef = useRef<DatePicker>(null);

  useDatepickerPortal();

  const handleChange = (d: Date | null) => {
    const normalized = d && defaultTime ? withDefaultTime(d, defaultTime) : d;
    setInternalDate(normalized);
    onChange(normalized ? normalized.toISOString() : undefined);
    // showTimeSelect keeps the popper open by default (expecting a time pick),
    // but when a defaultTime is supplied that pick is implicit — close so the
    // user sees inline validation that lands below the field. When no
    // defaultTime is configured, leave the popper open so the user can pick
    // their own time.
    if (normalized && defaultTime) pickerRef.current?.setOpen(false);
  };

  return (
    <Box
      sx={{ ".react-datepicker-wrapper": { maxWidth: { sm: "full", md: "60" } } }}
      className={inline ? "themed-datepicker-inline" : undefined}
    >
      <DatePickerGlobalStyles />
      <DatePicker
        ref={pickerRef}
        id={id}
        selected={selected}
        onChange={handleChange}
        onCalendarClose={onBlur}
        autoFocus={autoFocus}
        showTimeSelect
        timeIntervals={timeIntervals}
        timeCaption={resolvedTimeCaption}
        dateFormat="dd.MM.yyyy HH:mm"
        placeholderText={placeholder ?? "dd.mm.yyyy tt:mm"}
        customInput={<PickerCustomInput aria-label={ariaLabel} />}
        disabled={isDisabled}
        minDate={minDate ?? new Date()}
        maxDate={maxDate}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        portalId={inline ? undefined : PORTAL_ID}
        inline={inline}
        renderCustomHeader={(props) => <DatePickerHeader {...props} locale={intlLocale} />}
      />
    </Box>
  );
};
